import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Observable, BehaviorSubject, combineLatest, of, interval, Subscription} from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, shareReplay, startWith, tap, switchMap, take, filter } from 'rxjs/operators';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';

// Services
import { ValidationRunFacade } from '../../modules/core/services/validation-run/validationrun.facade';
import { PinnedValidationsComponent } from 'src/app/modules/pinned-validations/pinned-validations.component';
import { ToastService } from 'src/app/modules/core/services/toast/toast.service';
import { ValidationrunService } from 'src/app/modules/core/services/validation-run/validationrun.service';
import { AuthService } from 'src/app/modules/core/services/auth/auth.service';
import { DialogModule } from 'primeng/dialog';
import { HttpParams } from '@angular/common/http';

interface SortState {
  field: string;
  order: number;
}

type BulkAction = 'delete' | 'archive' | 'unarchive';

@Component({
  selector: 'qa-validations',
  standalone: true,
  imports: [
    PinnedValidationsComponent, ButtonModule, CommonModule, RouterModule, 
    TableModule, TooltipModule, InputTextModule, DatePickerModule, 
    FloatLabelModule, FormsModule, ReactiveFormsModule, TagModule, DialogModule
  ],
  templateUrl: './validations.component.html',
  styleUrls: ['./validations.component.scss'],
})

export class ValidationsComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private facade = inject(ValidationRunFacade);

  private validationrunService = inject(ValidationrunService); 
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private router = inject(Router);

  // From Facade
  loading$ = this.facade.loading$;
  
  rows$!: Observable<any[]>;
  totalRecords$!: Observable<number>;
  dataFetchError = false;

  filterForm!: FormGroup;
  readonly PAGE_SIZE = 20;

  private page$ = new BehaviorSubject({ page: 0, rows: this.PAGE_SIZE });
  private sort$ = new BehaviorSubject<SortState>({ field: 'start_time', order: -1 });

  // Edit Validation Name
  editingRowId: string | null = null;
  tempName: string = '';

  // Display validation Progress and Status
  private statusSubscription?: Subscription;

  // For delete dialog window
  confirmDeleteVisible = false;
  deleteTarget: any | null = null;
  deleting = false;

  // For stop dialog window
  confirmStopVisible = false;
  stopTarget: any | null = null;
  stopping = false;

  ngOnInit(): void {


    // Create Filters Reactive Form with fields: dateRange, Validation-run name, Dataset name
    this.filterForm = this.fb.group({
      dateRange: [null],
      name: [''],
      dataset: ['']
    });

    // Create Observable from Reactive Form
    const filters$ = this.filterForm.valueChanges.pipe(
      startWith(this.filterForm.value),
      debounceTime(300),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      // send to 0 page if user changed the filters
      tap(() => this.page$.next({ ...this.page$.value, page: 0 }))
    );

    // Listen to filters, page and sort and send request to backend
    const dataState$ = combineLatest([filters$, this.page$, this.sort$]).pipe(
      tap(() => this.dataFetchError = false),
      switchMap(([f, p, s]) => {
        // Filter from Facade
        const params = this.facade.ValidationRunFilters(f, p, s);
        // Use 'user' for validations loading
        return this.facade.getValidations('user', params).pipe(
          catchError(() => {
            this.dataFetchError = true;
            return of({ rows: [], total: 0 });
          })
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.rows$ = dataState$.pipe(map(res => res.rows));
    this.totalRecords$ = dataState$.pipe(map(res => res.total));

    this.statusSubscription = interval(30000).pipe(  // every 30 seconds
      switchMap(() => this.rows$.pipe(take(1))),
      filter(rows => rows.some(r => this.isLive(r)))
    ).subscribe(() => {
      this.page$.next({ ...this.page$.value });
    });

  }

  openDeleteDialog(row: any, event: Event): void {
    event.stopPropagation();
    this.deleteTarget = row;
    this.confirmDeleteVisible = true;
  }

  closeDeleteDialog(): void {
    this.confirmDeleteVisible = false;
    this.deleteTarget = null;
  }

  confirmDelete(): void {
    if (!this.deleteTarget || this.deleting) return;

    this.deleting = true;
    const row = this.deleteTarget;

    this.validationrunService.deleteValidation(row.id).pipe(take(1)).subscribe({
      next: () => {
        this.toastService.showSuccessWithHeader('Success', 'Validation was deleted successfully.');

        this.closeDeleteDialog();
        this.page$.next({ ...this.page$.value });   // reload table
        this.authService.init();                    // refresh user if needed
        this.deleting = false;
      },
      error: () => {
        this.toastService.showErrorWithHeader(
          'Error',
          'Failed to delete validation. It might be linked to other results or you might lack permissions.'
        );
        this.deleting = false;
      }
    });
  }

  startEdit(row: any): void {
    this.editingRowId = row.id;
    this.tempName = row.name_tag || '';
  }

  cancelEdit(): void {
    this.editingRowId = null;
  }

  saveEdit(row: any): void {
    const trimmedName = this.tempName.trim();
    if (trimmedName && trimmedName !== row.name_tag) {
      this.validationrunService.saveResultsName(row.id, trimmedName).subscribe({
        next: () => {
          row.name_tag = trimmedName;
          this.editingRowId = null;
          this.toastService.showSuccessWithHeader('Success', 'Name updated');
        },
        error: () => this.toastService.showErrorWithHeader('Error', 'Update failed')
      });
    } else {
      this.editingRowId = null;
    }
  }

  // Show the status progress
  getStatusDisplay(valrun: any): { label: string, severity: string, spinning?: boolean } {
    
    switch (valrun.status) {
      case 'DONE':
        return { label: 'Done', severity: 'success' };
      case 'CANCELLED':
        return { label: 'Cancelled', severity: 'secondary' };
      case 'ERROR':
        return { label: 'Error', severity: 'danger' };
      case 'SCHEDULED':
        return { label: 'Scheduled', severity: 'info' };
      case 'RUNNING':
        if (valrun.val_type === 'both') {
          // both progresses at 100 but files not yet written
          if (valrun.progress === 100 && valrun.progress_spatial === 100) {
            return { label: 'Generating plots...', severity: 'warn', spinning: true };
          }
          // spatial done, temporal still running
          if (valrun.progress_spatial === 100 && valrun.progress < 100) {
            return { label: `Running\nTemporal: ${valrun.progress}%\nSpatial: done`, severity: 'warn', spinning: true };
          }
          return { label: `Running\nTemporal: ${valrun.progress}%\nSpatial: ${valrun.progress_spatial}%`, severity: 'warn', spinning: true };
        }
        if (valrun.val_type === 'spatial') {
          if (valrun.progress_spatial === 100) {
            return { label: 'Generating plots...', severity: 'warn', spinning: true };
          }
          return { label: `Running\nSpatial: ${valrun.progress_spatial}%`, severity: 'warn', spinning: true };
        }
        if (valrun.progress === 100) {
          return { label: 'Generating plots...', severity: 'warn', spinning: true };
        }
        return { label: `Running ${valrun.progress}%`, severity: 'warn', spinning: true };

      default:
        return { label: 'Unknown', severity: 'secondary' };
    }
  }

  // Stop Validation (if we have running validation)
  openStopDialog(row: any, event: Event): void {
    event.stopPropagation();
    this.stopTarget = row;
    this.confirmStopVisible = true;
  }

  closeStopDialog(): void {
    this.confirmStopVisible = false;
    this.stopTarget = null;
  }

  confirmStop(): void {
    if (!this.stopTarget || this.stopping) return;

    this.stopping = true;
    const row = this.stopTarget;

    this.validationrunService.stopValidation(row.id).pipe(take(1)).subscribe({
      next: () => {
        this.toastService.showSuccessWithHeader('Success', 'Validation stop requested.');
        this.closeStopDialog();
        this.page$.next({ ...this.page$.value }); // refresh table
        this.stopping = false;
      },
      error: () => {
        this.toastService.showErrorWithHeader('Error', 'Could not stop validation.');
        this.stopping = false;
      }
    });
  }

  isStoppable(valrun: any): boolean {

    if (valrun.status === 'SCHEDULED') return false;  
    if (valrun.status !== 'RUNNING') return false;
    
    // Generating plots — no celery tasks to stop
    if (valrun.val_type === 'spatial' && valrun.progress_spatial === 100) return false;
    if (valrun.val_type === 'temporal' && valrun.progress === 100) return false;
    if (valrun.val_type === 'both' && valrun.progress === 100 && valrun.progress_spatial === 100) return false;
    
    return true;
  }

  isLive(valrun: any): boolean {
    return valrun.status === 'RUNNING' || valrun.status === 'SCHEDULED';
  }

  onPageChange(event: any): void {
    this.page$.next({ page: event.first / event.rows, rows: event.rows });
  }

  onSortChange(event: any): void {
    this.sort$.next({ field: event.field, order: event.order });
  }

  // View Results page
  ViewResults(row: any, event?: Event) {
    const target = event?.target as HTMLElement | undefined;
    if (target?.closest('button, a, input, textarea, .p-inputtext')) return;
    this.router.navigate(['/validation-result', row.id]);
  }
  loadSettings(row: any, event: Event): void {
    event.stopPropagation();

    const params = new HttpParams().set('validation_id', row.id);

    this.validationrunService.copyValidation(params)
      .pipe(take(1))
      .subscribe({
        next: (data: any) => {
          const newId = data.run_id; // same as ButtonsComponent
          this.router.navigate(['/validate'], { queryParams: { validation_id: newId } });
        },
        error: () => {
          this.toastService.showErrorWithHeader('Error', 'Could not load settings.');
        }
      });
  }

  selectedRows: any[] = [];

  isRowSelectable = (event: { data: any; index: number }): boolean =>
    this.isSelectable(event.data);

  isSelectable(row: any): boolean {
    return !this.isLive(row) && !row.doi && !row.is_published;
  }

  // Bulk actions
  bulkAction: BulkAction | null = null;
  bulkVisible = false;
  bulkBusy = false;

  private eligibleFor(action: BulkAction): any[] {
    return this.selectedRows.filter(r =>
      action === 'unarchive' ? !!r.is_archived : !r.is_archived
    );
  }

  get deletable(): any[]    { return this.eligibleFor('delete'); }
  get archivable(): any[]   { return this.eligibleFor('archive'); }
  get unarchivable(): any[] { return this.eligibleFor('unarchive'); }

  get bulkTargets(): any[] {
    return this.bulkAction ? this.eligibleFor(this.bulkAction) : [];
  }

  get bulkLabel(): string {
    switch (this.bulkAction) {
      case 'delete':    return 'permanently delete';
      case 'archive':   return 'archive';
      case 'unarchive': return 'un-archive';
      default:          return '';
    }
  }

  get bulkHeader(): string {
    switch (this.bulkAction) {
      case 'delete':    return 'Delete validations';
      case 'archive':   return 'Archive validations';
      case 'unarchive': return 'Un-archive validations';
      default:          return '';
    }
  }

  openBulkDialog(action: BulkAction): void {
    this.bulkAction = action;
    this.bulkVisible = true;
  }

  closeBulkDialog(): void {
    this.bulkVisible = false;
    this.bulkAction = null;
  }

  confirmBulk(): void {
    const targets = this.bulkTargets;
    if (!this.bulkAction || this.bulkBusy || !targets.length) return;

    this.bulkBusy = true;
    const ids = targets.map(r => r.id);
    const action = this.bulkAction;

    const request$ = action === 'delete'
      ? this.validationrunService.removeMultipleValidation(ids)
      : this.validationrunService.archiveMultipleValidation(ids, action === 'archive');

    request$.pipe(take(1)).subscribe({
      next: () => {
        this.toastService.showSuccessWithHeader(
          'Success',
          `${ids.length} validation(s) updated.`
        );
        this.finishBulk();
      },
      error: () => {
        this.toastService.showErrorWithHeader('Error', 'Bulk operation failed.');
        this.bulkBusy = false;
      }
    });
  }

  private finishBulk(): void {
    this.selectedRows = [];
    this.closeBulkDialog();
    this.facade.clearCache();
    this.page$.next({ ...this.page$.value });
    this.authService.init();
    this.bulkBusy = false;
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
  }
}